/**
 * Rozložení mřížky kamer.
 *
 * Dvě nezávislé osy:
 *  - **režim** (volba uživatele, přežívá refresh v `useUIStore`)
 *  - **fullscreen** (stav wrapperu)
 *
 * | režim  | normálně          | fullscreen              |
 * |--------|-------------------|-------------------------|
 * | `auto` | vedle sebe        | pod sebou, snap scroll  |
 * | `quad` | mřížka 2×2        | mřížka 2×2 na celou plochu |
 *
 * Čistá funkce, protože komponentové testy tenhle projekt nespouští
 * (`include: ["src/**\/*.test.ts"]`, žádné jsdom) — logika, která má být pokrytá,
 * musí být v `.ts`.
 */

/** Volba uživatele. */
export type CameraLayoutMode = "auto" | "quad";

/** Co dostane dlaždice. Fullscreen varianty jsou zvlášť, protože mění výšku buňky. */
export type CameraTileMode = "grid" | "stacked" | "quad" | "quad-fs";

export const tileMode = (mode: CameraLayoutMode, isFullscreen: boolean): CameraTileMode => {
  if (mode === "quad") return isFullscreen ? "quad-fs" : "quad";
  return isFullscreen ? "stacked" : "grid";
};

export const cameraContainerClass = (mode: CameraLayoutMode, isFullscreen: boolean): string => {
  if (mode === "quad") {
    // `auto-rows-fr` místo `grid-rows-2`: při čtyřech kamerách vyjdou dvě stejně
    // vysoké řady, při šesti tři a scrolluje se — pevné dvě řady by je zmáčkly.
    return isFullscreen
      ? "grid h-full w-full grid-cols-2 auto-rows-fr gap-1 overflow-y-auto overscroll-contain"
      : "grid w-full grid-cols-2 gap-1";
  }
  return isFullscreen
    ? "flex h-full w-full snap-y snap-mandatory flex-col gap-1 overflow-y-auto overscroll-contain"
    : // `flex-wrap` + `basis-1/2` na dlaždici = pod 640 px dvě kamery na řádek. Bez
      // zalomení se čtyři kamery na telefonu dělily o šířku displeje a vycházel
      // z toho ~91 × 51 px proužek. Mezera musí zůstat nulová — s gapem by
      // `basis-1/2` přeteklo na jednu na řádek.
      "flex w-full flex-row flex-wrap items-center justify-center";
};

/**
 * Vyplňuje obraz celou buňku (a jsou tedy vidět PTZ ovládání a jméno kamery)?
 *
 * V režimu `grid` ne — tam si video drží přirozený poměr a určuje výšku dlaždice.
 * Všude jinde má buňka danou výšku, takže se video musí vejít dovnitř.
 */
export const tileFills = (mode: CameraTileMode, spotlighted: boolean): boolean =>
  spotlighted || mode !== "grid";
